import { map, filter, forEach, groupBy, uniq, isEmpty, has, get, set } from 'lodash';
import { ValidationTypes } from '../constants/schemaEditor';

const objectPropertyNamePattern = /^(\w+\.)+\w+$/;

/**
 * Detects if a schema follows JSON Schema specification
 * @param {Object} schema - The schema object to check
 * @returns {boolean} - True if the schema follows JSON Schema format
 */
export function isJsonSchema(schema) {
  // JSON Schema detection based on specification
  // A valid JSON Schema should have 'type' and 'properties' at the root level
  return !!(
    schema &&
    typeof schema === 'object' &&
    schema.type === 'object' &&
    schema.properties &&
    typeof schema.properties === 'object'
  );
}

export function buildOutputValidationSchema(outputValidations, schema = null) {
  const validOutputValidations = filter(
    outputValidations,
    ({ type, attrs, options }) =>
      !(
        isEmpty(attrs) ||
        (type === ValidationTypes.FORMAT && isEmpty(options.formats)) ||
        ((type === ValidationTypes.RANGE || type === ValidationTypes.SIZE) &&
          !has(options, 'min') &&
          !has(options, 'max')) ||
        (type === ValidationTypes.ENUM && isEmpty(options.values)) ||
        (type === ValidationTypes.PATTERN && !options.pattern)
      ),
  );
  let parentNames = uniq(map(schema, '__parentName'));
  parentNames = isEmpty(parentNames) ? [null] : parentNames;
  const result = {};
  const attrsByName = groupBy(schema, '__name');

  function getAttrPath(attrName, getArrayItemsPath = false, isParentObject = false) {
    // create the full path in result (result.properties['a'].properties['b'] = {})
    if (!has(attrsByName, attrName)) {
      if (isParentObject) {
        return map(attrName.split('.'), (pathPart) => `properties["${pathPart}"]`).join('.');
      }

      return null;
    }

    const pathParts = [];

    for (let currentParentName = attrName; currentParentName; ) {
      const attr = attrsByName[currentParentName][0];
      currentParentName = attr.__parentName;
      const parentType = currentParentName && attrsByName[currentParentName][0].__type;
      const isParentArray = parentType && parentType.startsWith('array');
      let pathPart = `${isParentArray ? 'properties.items' : 'properties'}["${
        attr.__displayName
      }"]`;
      pathParts.unshift(pathPart);
    }

    const parentType = attrsByName[attrName][0].__type;

    if (getArrayItemsPath && parentType.startsWith('array')) {
      pathParts.push('properties.items');
    }

    return pathParts.join('.');
  }

  function setAttrsInResult(attrs, attrValidation, parentName, isParentObject = false) {
    // if some custom attrs are properties of an object, deal with them first
    if (!parentName) {
      forEach(attrs, (attrName) => {
        if (!has(attrsByName, attrName) && objectPropertyNamePattern.test(attrName)) {
          const lastDotIndex = attrName.lastIndexOf('.');
          setAttrsInResult([attrName], attrValidation, attrName.slice(0, lastDotIndex), true);
        }
      });
    }

    const currentParentsChildren = map(
      filter(
        attrs,
        (attrName) =>
          (has(attrsByName, attrName) && attrsByName[attrName][0].__parentName === parentName) ||
          (!has(attrsByName, attrName) &&
            ((!parentName && !objectPropertyNamePattern.test(attrName)) ||
              (parentName &&
                objectPropertyNamePattern.test(attrName) &&
                attrName.startsWith(parentName)))),
      ),
      (attrName) => {
        if (has(attrsByName, attrName)) {
          return attrsByName[attrName][0].__displayName;
        }

        if (objectPropertyNamePattern.test(attrName)) {
          const lastDotIndex = attrName.lastIndexOf('.');
          return attrName.slice(lastDotIndex + 1);
        }

        return attrName;
      },
    );

    if (isEmpty(currentParentsChildren)) {
      return;
    }

    const path = getAttrPath(parentName, true, isParentObject);
    let currentValidationSchema;

    if (!path) {
      currentValidationSchema = result;
    } else if (has(result, path)) {
      currentValidationSchema = get(result, path);
    } else {
      currentValidationSchema = {};
      set(result, path, currentValidationSchema);
    }

    if (isParentObject) {
      currentValidationSchema.type = 'object';
    }

    if (attrValidation === 'required') {
      currentValidationSchema.required = currentParentsChildren;
    } else {
      if (!currentValidationSchema.properties) {
        currentValidationSchema.properties = {};
      }

      forEach(currentParentsChildren, (attr) => {
        currentValidationSchema.properties[attr] = {
          ...currentValidationSchema.properties[attr],
          ...attrValidation,
        };
      });
    }
  }

  forEach(validOutputValidations, (validation) =>
    forEach(parentNames, (parentName) => {
      const { type, attrs, options } = validation;
      let attrValidation = {};

      switch (type) {
        case ValidationTypes.REQUIRED:
          setAttrsInResult(attrs, 'required', parentName);
          break;

        case ValidationTypes.SIZE:
          if (options.min) {
            attrValidation.minItems = toFloatIfPossible(options.min);
          }

          if (options.max) {
            attrValidation.maxItems = toFloatIfPossible(options.max);
          }

          setAttrsInResult(attrs, attrValidation, parentName);
          break;

        case ValidationTypes.FORMAT:
          setAttrsInResult(
            attrs,
            options.formats.length === 1 ? { ...options.formats[0] } : { anyOf: options.formats },
            parentName,
          );
          break;

        case ValidationTypes.ENUM:
          setAttrsInResult(attrs, { type: 'string', enum: options.values }, parentName);
          break;

        case ValidationTypes.PATTERN:
          setAttrsInResult(attrs, { type: 'string', pattern: options.pattern }, parentName);
          break;

        case ValidationTypes.RANGE:
          if (options.min) {
            attrValidation.minimum = toFloatIfPossible(options.min);
          }

          if (options.max) {
            attrValidation.maximum = toFloatIfPossible(options.max);
          }

          setAttrsInResult(attrs, attrValidation, parentName);
          break;

        default:
      }
    }),
  );

  // set "type" of object and array attrs
  forEach(schema, (attr) => {
    const { __type, __name } = attr;
    const isAttrArray = __type && __type.startsWith('array');

    if (__type === 'object' || isAttrArray) {
      const path = getAttrPath(__name);
      const attrValidationSchema = get(result, path);

      if (attrValidationSchema) {
        if (isAttrArray) {
          const arrayItemsPath = path + '.properties.items';
          const itemsValidationSchema = get(result, arrayItemsPath);

          if (itemsValidationSchema) {
            const isObjectArray = has(itemsValidationSchema, 'properties') ? true : false;

            if (!attrValidationSchema.type && !attrValidationSchema.anyOf) {
              attrValidationSchema.type = 'array';
            }

            if (!itemsValidationSchema.type && !itemsValidationSchema.anyOf) {
              itemsValidationSchema.type = isObjectArray ? 'object' : __type;
            }
          }
        } else if (!attrValidationSchema.type && !attrValidationSchema.anyOf) {
          attrValidationSchema.type = __type;
        }
      }
    }
  });

  return result;
}

function toFloatIfPossible(value) {
  const float = parseFloat(value, 10);

  return isNaN(float) ? value : float;
}
